import FestivalVoucherFloatingIcon from '@/components/home/FestivalVoucherFloatingIcon';
import getCommonLayout from '@/lib/Layouts/commonLayouts';
import localFont from 'next/font/local';
import MessageModal from '@/components/common/modals/MessageModal';
import nextI18NextConfig from '@/next-i18next.config.js';
import OneSignalComponent from '@/components/OneSignalComponent';
import Providers from '@/lib/providers';
import React, { useEffect } from 'react';
import RecordUserSession from '@/components/common/RecordUserSession60Seconds';
import ReferralsFloatingIcon from '@/components/home/ReferralsFloatingIcon';
import useMonitorWindowWidth from '@/hooks/useMonitorWindowWidth';
import VoucherFestivalFloatingIcon from '@/components/home/VoucherFestivalFloatingIcon';
import { _AppProps, DefaultServerSideProps } from '@/types/props/pages';
import { appWithTranslation, UserConfig } from 'next-i18next';
import { config } from '@fortawesome/fontawesome-svg-core';
import { QueryClient } from '@tanstack/react-query';
import '@/styles/tailwinds.globals.css';
import '@/styles/scss/global.scss';
import 'react-multi-carousel/lib/styles.css';
import '@fortawesome/fontawesome-svg-core/styles.css';
import 'react-toastify/dist/ReactToastify.css';
import { usePathname } from 'next/navigation';
import MarketingWinBottleSignUp from '@/components/marketing/MarketingWinBottleSignUp';
import KlaviyoLimitedSpecialOffer from '@/components/Klaviyo/KlaviyoLimitedSpecialOffer';
import OnBoardingModal from '@/components/auth/onboarding/OnBoardingModal/OnBoardingModal';

const emptyInitialI18NextConfig: UserConfig = {
  i18n: {
    defaultLocale: nextI18NextConfig.i18n.defaultLocale,
    locales: nextI18NextConfig.i18n.locales,
  },
};

config.autoAddCss = false;

// Plus Jakarta Sans
const plusJakartaSans = localFont({
  src: [
    {
      path: '../public/fonts/PlusJakartaSans-VariableFont_wght.ttf',
      style: 'normal',
    },
    {
      path: '../public/fonts/PlusJakartaSans-Italic-VariableFont_wght.ttf',
      style: 'italic',
    },
  ],
  variable: '--font-plus-jakarta-sans',
});

// Prata (no es variable, es una fuente regular)
const prata = localFont({
  src: '../public/fonts/Prata-Regular.ttf',
  variable: '--font-prata',
});

// Work Sans
const workSans = localFont({
  src: [
    {
      path: '../public/fonts/WorkSans-VariableFont_wght.ttf',
      style: 'normal',
    },
    {
      path: '../public/fonts/WorkSans-Italic-VariableFont_wght.ttf',
      style: 'italic',
    },
  ],
  variable: '--font-work-sans',
});

// Inter
const inter = localFont({
  src: [
    {
      path: '../public/fonts/Inter-VariableFont_opsz,wght.ttf',
      style: 'normal',
    },
    {
      path: '../public/fonts/Inter-Italic-VariableFont_opsz,wght.ttf',
      style: 'italic',
    },
  ],
  variable: '--font-inter',
});

function App({ Component, pageProps }: _AppProps) {
  const pathname = usePathname();
  const pageDefaultProps: DefaultServerSideProps = { ...pageProps };
  const [queryClient] = React.useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000,
          },
        },
      })
  );

  const renderWithLayout = Component.getLayout || getCommonLayout('default');

  useMonitorWindowWidth();

  useEffect(() => {
    document.body.style.setProperty('--font-work-sans', workSans.style.fontFamily);
    document.body.style.setProperty('--font-prata', prata.style.fontFamily);
    document.body.style.setProperty('--font-plus-jakarta-sans', plusJakartaSans.style.fontFamily);
    document.body.style.setProperty('--font-inter', inter.style.fontFamily);
  }, []);

  return (
    <div
      className={`${workSans.variable} ${prata.variable} ${plusJakartaSans.variable} ${inter.variable} tw-font-plusJakartaSans`}
    >
      <Providers pageProps={pageProps} queryClient={queryClient}>
        <OneSignalComponent />
        {pathname?.startsWith('/auth') === false && <ReferralsFloatingIcon />}
        {pathname?.startsWith('/auth') === false && <OnBoardingModal />}
        <FestivalVoucherFloatingIcon />
        <VoucherFestivalFloatingIcon />
        <MessageModal />
        <RecordUserSession />
        <MarketingWinBottleSignUp />

        {renderWithLayout(<Component {...pageProps} />, pageProps)}
      </Providers>
    </div>
  );
}

export default appWithTranslation(App, emptyInitialI18NextConfig);
